import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Zap, Search, 
  ExternalLink, Clock, RefreshCw, 
  ArrowUpRight, ArrowDownRight, Building2, Landmark, Globe, Scale, 
  FileText, UserCircle, LayoutGrid, Eye, EyeOff, ChevronDown, ChevronUp,
  Calendar, HelpCircle, Lightbulb, RotateCcw, Settings, X, Plus,
  ChevronRight
} from 'lucide-react';
import { useUserPreferences } from '../context/UserPreferencesContext';
import Sparkline, { generateSparklineData } from '../components/Sparkline';
import './DashboardPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// =============================================================================
// FEAR & GREED GAUGE
// =============================================================================
function FearGreedGauge({ value = 72 }) {
  const getLabel = (v) => {
    if (v <= 20) return 'Extreme Fear';
    if (v <= 40) return 'Fear';
    if (v <= 60) return 'Neutral';
    if (v <= 80) return 'Greed';
    return 'Extreme Greed';
  };
  
  const getColor = (v) => {
    if (v <= 20) return '#ef4444';
    if (v <= 40) return '#f97316';
    if (v <= 60) return '#eab308';
    if (v <= 80) return '#22c55e';
    return '#10b981';
  };
  
  const angle = (value / 100) * 180 - 90;
  const color = getColor(value);
  const label = getLabel(value);
  
  return (
    <div className="fear-greed-gauge">
      <svg viewBox="0 0 200 120" className="gauge-svg">
        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="25%" stopColor="#f97316" />
            <stop offset="50%" stopColor="#eab308" />
            <stop offset="75%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="14" strokeLinecap="round" />
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#gaugeGradient)" strokeWidth="14" strokeLinecap="round" />
        <g transform={`rotate(${angle} 100 100)`}>
          <line x1="100" y1="100" x2="100" y2="35" stroke={color} strokeWidth="3" strokeLinecap="round" />
          <circle cx="100" cy="100" r="6" fill={color} />
          <circle cx="100" cy="100" r="3" fill="#0a0a0f" />
        </g>
        <text x="25" y="115" fill="rgba(255,255,255,0.4)" fontSize="10">Fear</text>
        <text x="175" y="115" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="end">Greed</text>
      </svg>
      <div className="gauge-info">
        <span className="gauge-value" style={{ color }}>{value}</span>
        <span className="gauge-label" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}

// =============================================================================
// POLITICAL INTELLIGENCE NAV
// =============================================================================
function PoliticalIntelligenceNav({ navigate }) {
  const navItems = [
    { 
      id: 'congress', 
      label: 'Congress Trading', 
      icon: Landmark, 
      path: '/congress',
      description: 'STOCK Act Disclosures',
      stat: '1,847 trades',
      trend: '+23%'
    },
    { 
      id: 'insider', 
      label: 'Insider Trading', 
      icon: UserCircle, 
      path: '/insider',
      description: 'SEC Form 4 Filings',
      stat: '3,291 filings',
      trend: '+12%'
    },
    { 
      id: 'institutional', 
      label: 'Institutional Holdings', 
      icon: Building2, 
      path: '/institutional',
      description: '13F Filings',
      stat: '$2.4T tracked',
      trend: '+8%'
    },
  ];

  return (
    <div className="political-intel-nav">
      <div className="intel-nav-header">
        <div className="intel-nav-title">
          <Zap size={18} className="intel-icon" />
          <span>Political Intelligence</span>
        </div>
        <span className="intel-badge">EDGE</span>
      </div>
      <div className="intel-nav-cards">
        {navItems.map(item => (
          <div 
            key={item.id} 
            className="intel-nav-card"
            onClick={() => navigate(item.path)}
          >
            <div className="intel-card-icon">
              <item.icon size={20} />
            </div>
            <div className="intel-card-content">
              <span className="intel-card-label">{item.label}</span>
              <span className="intel-card-desc">{item.description}</span>
            </div>
            <div className="intel-card-stats">
              <span className="intel-stat-value">{item.stat}</span>
              <span className="intel-stat-trend positive">{item.trend}</span>
            </div>
            <ChevronRight size={16} className="intel-card-arrow" />
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// WHY MOVING CARD (Customizable)
// =============================================================================
function WhyMovingCard({ trackedSymbols, onUpdateSymbols, allMovers, onStockClick }) {
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const explanations = {
    'NVDA': 'Strong demand for AI chips continues. Q4 data center revenue beat expectations by 12%. Analysts raising price targets.',
    'TSLA': 'China EV competition intensifying. Model Y price cuts in Europe signal margin pressure. Delivery guidance unchanged.',
    'AAPL': 'Vision Pro sales exceeding expectations. Services revenue growth accelerating. iPhone 16 cycle building momentum.',
    'AMD': 'MI300X AI accelerator gaining enterprise traction. Gaming segment recovery. Server CPU market share expanding.',
    'MSFT': 'Azure growth steady at 29%. Copilot enterprise adoption slower than expected. Gaming division integration ongoing.',
    'META': 'Reels monetization improving. AI infrastructure investments paying off. WhatsApp business features expanding.',
    'BTC': 'Institutional adoption accelerating with ETF inflows. Halving cycle dynamics supporting price. On-chain metrics bullish.',
    'ETH': 'ETF approval driving institutional interest. Layer 2 adoption growing. Staking yields remain attractive.',
    'SOL': 'DeFi TVL hitting new highs. Network performance improvements. Memecoin activity driving transaction volume.',
  };

  const trackedMovers = trackedSymbols
    .map(sym => allMovers.find(m => m.symbol === sym))
    .filter(Boolean);

  const availableSymbols = allMovers
    .filter(m => !trackedSymbols.includes(m.symbol))
    .filter(m => m.symbol.toLowerCase().includes(searchTerm.toLowerCase()) || 
                 m.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const addSymbol = (symbol) => {
    if (!trackedSymbols.includes(symbol)) {
      onUpdateSymbols([...trackedSymbols, symbol]);
    }
    setSearchTerm('');
  };

  const removeSymbol = (symbol) => {
    onUpdateSymbols(trackedSymbols.filter(s => s !== symbol));
  };

  return (
    <div className="card card-why-moving">
      <div className="card-header">
        <h3><HelpCircle size={16} /> Why Are They Moving?</h3>
        <button className="icon-btn" onClick={() => setIsEditing(!isEditing)} title="Edit tracked stocks">
          {isEditing ? <X size={14} /> : <Settings size={14} />}
        </button>
      </div>

      {isEditing ? (
        <div className="tracking-editor">
          <div className="tracking-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search to add..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {searchTerm && (
            <div className="tracking-suggestions">
              {availableSymbols.slice(0, 5).map(m => (
                <button key={m.symbol} className="suggestion-item" onClick={() => addSymbol(m.symbol)}>
                  <Plus size={12} />
                  <span className="suggestion-symbol">{m.symbol}</span>
                  <span className="suggestion-name">{m.name}</span>
                </button>
              ))}
              {availableSymbols.length === 0 && (
                <div className="no-results">No matches found</div>
              )}
            </div>
          )}

          <div className="tracking-current">
            <span className="tracking-label">Currently tracking:</span>
            <div className="tracking-chips">
              {trackedSymbols.map(sym => (
                <span key={sym} className="tracking-chip">
                  {sym}
                  <button onClick={() => removeSymbol(sym)}><X size={10} /></button>
                </span>
              ))}
              {trackedSymbols.length === 0 && (
                <span className="tracking-empty">No stocks tracked</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="why-moving-list">
          {trackedMovers.length === 0 ? (
            <div className="empty-state">
              <p>No stocks tracked. Click <Settings size={12} /> to add.</p>
            </div>
          ) : (
            trackedMovers.map(mover => {
              const isPositive = mover.changePercent >= 0;
              return (
                <div key={mover.symbol} className="why-item" onClick={() => onStockClick(mover.symbol)}>
                  <div className="why-item-header">
                    <span className="why-symbol">{mover.symbol}</span>
                    <span className={`why-change ${isPositive ? 'positive' : 'negative'}`}>
                      {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {isPositive ? '+' : ''}{mover.changePercent.toFixed(2)}%
                    </span>
                  </div>
                  <p className="why-explanation">
                    {explanations[mover.symbol] || `${mover.symbol} is experiencing ${isPositive ? 'bullish' : 'bearish'} momentum due to market conditions.`}
                  </p>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EARNINGS CALENDAR CARD (Customizable)
// =============================================================================
function EarningsCalendarCard({ trackedSymbols, onUpdateSymbols, onStockClick }) {
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const allEarnings = [
    { symbol: 'AAPL', name: 'Apple Inc', date: 'Feb 6', time: 'AMC', estimate: '$2.10' },
    { symbol: 'GOOGL', name: 'Alphabet Inc', date: 'Feb 4', time: 'AMC', estimate: '$1.59' },
    { symbol: 'AMZN', name: 'Amazon.com', date: 'Feb 6', time: 'AMC', estimate: '$1.49' },
    { symbol: 'META', name: 'Meta Platforms', date: 'Feb 5', time: 'AMC', estimate: '$5.25' },
    { symbol: 'MSFT', name: 'Microsoft Corp', date: 'Feb 4', time: 'AMC', estimate: '$2.82' },
    { symbol: 'NVDA', name: 'NVIDIA Corp', date: 'Feb 26', time: 'AMC', estimate: '$4.60' },
    { symbol: 'TSLA', name: 'Tesla Inc', date: 'Jan 29', time: 'AMC', estimate: '$0.76' },
    { symbol: 'AMD', name: 'Advanced Micro', date: 'Feb 4', time: 'AMC', estimate: '$0.77' },
    { symbol: 'NFLX', name: 'Netflix Inc', date: 'Jan 21', time: 'AMC', estimate: '$4.20' },
    { symbol: 'DIS', name: 'Walt Disney', date: 'Feb 5', time: 'BMO', estimate: '$1.45' },
  ];

  const trackedEarnings = trackedSymbols
    .map(sym => allEarnings.find(e => e.symbol === sym))
    .filter(Boolean);

  const availableEarnings = allEarnings
    .filter(e => !trackedSymbols.includes(e.symbol))
    .filter(e => e.symbol.toLowerCase().includes(searchTerm.toLowerCase()) || 
                 e.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const addSymbol = (symbol) => {
    if (!trackedSymbols.includes(symbol)) {
      onUpdateSymbols([...trackedSymbols, symbol]);
    }
    setSearchTerm('');
  };

  const removeSymbol = (symbol) => {
    onUpdateSymbols(trackedSymbols.filter(s => s !== symbol));
  };

  return (
    <div className="card card-earnings">
      <div className="card-header">
        <h3><Calendar size={16} /> Earnings Calendar</h3>
        <button className="icon-btn" onClick={() => setIsEditing(!isEditing)} title="Edit tracked earnings">
          {isEditing ? <X size={14} /> : <Settings size={14} />}
        </button>
      </div>

      {isEditing ? (
        <div className="tracking-editor">
          <div className="tracking-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search to add..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {searchTerm && (
            <div className="tracking-suggestions">
              {availableEarnings.slice(0, 5).map(e => (
                <button key={e.symbol} className="suggestion-item" onClick={() => addSymbol(e.symbol)}>
                  <Plus size={12} />
                  <span className="suggestion-symbol">{e.symbol}</span>
                  <span className="suggestion-name">{e.name}</span>
                  <span className="suggestion-date">{e.date}</span>
                </button>
              ))}
              {availableEarnings.length === 0 && (
                <div className="no-results">No matches found</div>
              )}
            </div>
          )}

          <div className="tracking-current">
            <span className="tracking-label">Tracking:</span>
            <div className="tracking-chips">
              {trackedSymbols.map(sym => (
                <span key={sym} className="tracking-chip">
                  {sym}
                  <button onClick={() => removeSymbol(sym)}><X size={10} /></button>
                </span>
              ))}
              {trackedSymbols.length === 0 && (
                <span className="tracking-empty">No earnings tracked</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="earnings-list-scroll">
          {trackedEarnings.length === 0 ? (
            <div className="empty-state">
              <p>No earnings tracked. Click <Settings size={12} /> to add.</p>
            </div>
          ) : (
            trackedEarnings.map(e => (
              <div key={e.symbol} className="earning-row" onClick={() => onStockClick(e.symbol)}>
                <div className="earning-info">
                  <span className="earning-symbol">{e.symbol}</span>
                  <span className="earning-name">{e.name}</span>
                </div>
                <div className="earning-details">
                  <span className="earning-date">{e.date}</span>
                  <span className="earning-time">{e.time}</span>
                </div>
                <span className="earning-estimate">{e.estimate}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// WIDGET CUSTOMIZER (Simplified)
// =============================================================================
function WidgetCustomizer({ widgets, onToggle, isOpen, onToggleOpen, onResetLayout }) {
  const widgetList = [
    { key: 'fearGreed', label: 'Fear & Greed' },
    { key: 'economic', label: 'Economic Data' },
    { key: 'stockMovers', label: 'Stock Movers' },
    { key: 'cryptoMovers', label: 'Crypto Movers' },
    { key: 'whyMoving', label: 'Why Moving' },
    { key: 'aiInsights', label: 'AI Insights' },
    { key: 'marketNews', label: 'Market News' },
    { key: 'cryptoNews', label: 'Crypto News' },
    { key: 'researchTip', label: 'Research Tip' },
    { key: 'earningsCalendar', label: 'Earnings Calendar' },
    { key: 'institutional', label: 'Institutional' },
    { key: 'policyWatch', label: 'Policy Watch' },
    { key: 'watchlist', label: 'Watchlist' },
  ];

  return (
    <div className="widget-customizer">
      <div className="customizer-buttons">
        <button className="customizer-toggle" onClick={onToggleOpen}>
          <LayoutGrid size={16} />
          Customize Widgets
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
      
      {isOpen && (
        <div className="customizer-panel">
          <div className="customizer-header">
            <p className="customizer-hint">Toggle widgets to show or hide</p>
            <button className="reset-layout-btn" onClick={onResetLayout}>
              <RotateCcw size={12} />
              Reset
            </button>
          </div>
          <div className="widget-toggles">
            {widgetList.map(({ key, label }) => {
              const widget = widgets[key];
              if (!widget) return null;
              return (
                <button
                  key={key}
                  className={`widget-toggle ${widget.enabled ? 'enabled' : ''}`}
                  onClick={() => onToggle(key)}
                >
                  {widget.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN DASHBOARD
// =============================================================================
function DashboardPage({ onOpenModal }) {
  const navigate = useNavigate();
  const { formatPrice, t } = useUserPreferences();
  const [economicData, setEconomicData] = useState(null);
  const [economicLoading, setEconomicLoading] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);

  // Widget visibility state
  const getDefaultWidgets = () => ({
    fearGreed: { enabled: true },
    economic: { enabled: true },
    stockMovers: { enabled: true },
    cryptoMovers: { enabled: true },
    whyMoving: { enabled: true },
    aiInsights: { enabled: true },
    marketNews: { enabled: true },
    cryptoNews: { enabled: true },
    researchTip: { enabled: true },
    earningsCalendar: { enabled: false },
    institutional: { enabled: true },
    policyWatch: { enabled: true },
    watchlist: { enabled: false },
  });

  // Tracked symbols for customizable widgets
  const [whyMovingSymbols, setWhyMovingSymbols] = useState(() => {
    const saved = localStorage.getItem('whyMovingSymbols');
    return saved ? JSON.parse(saved) : ['NVDA', 'TSLA', 'BTC'];
  });

  const [earningsSymbols, setEarningsSymbols] = useState(() => {
    const saved = localStorage.getItem('earningsSymbols');
    return saved ? JSON.parse(saved) : ['AAPL', 'GOOGL', 'AMZN', 'META'];
  });

  // Save tracked symbols to localStorage
  useEffect(() => {
    localStorage.setItem('whyMovingSymbols', JSON.stringify(whyMovingSymbols));
  }, [whyMovingSymbols]);

  useEffect(() => {
    localStorage.setItem('earningsSymbols', JSON.stringify(earningsSymbols));
  }, [earningsSymbols]);

  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboardWidgets');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const defaults = getDefaultWidgets();
        return Object.keys(defaults).reduce((acc, key) => {
          acc[key] = { enabled: parsed[key]?.enabled ?? defaults[key].enabled };
          return acc;
        }, {});
      } catch (e) {
        return getDefaultWidgets();
      }
    }
    return getDefaultWidgets();
  });

  useEffect(() => {
    localStorage.setItem('dashboardWidgets', JSON.stringify(widgets));
  }, [widgets]);

  const toggleWidget = (key) => {
    setWidgets(prev => ({
      ...prev,
      [key]: { enabled: !prev[key].enabled }
    }));
  };

  const handleResetLayout = () => {
    setWidgets(getDefaultWidgets());
  };

  // Market data
  const marketMovers = useMemo(() => {
    const raw = [
      { symbol: 'NVDA', name: 'NVIDIA Corp', price: 142.50, changePercent: 3.81 },
      { symbol: 'TSLA', name: 'Tesla Inc', price: 352.80, changePercent: -2.34 },
      { symbol: 'AAPL', name: 'Apple Inc', price: 191.24, changePercent: 1.15 },
      { symbol: 'AMD', name: 'Advanced Micro', price: 138.90, changePercent: 3.48 },
      { symbol: 'MSFT', name: 'Microsoft Corp', price: 432.15, changePercent: -0.74 },
      { symbol: 'META', name: 'Meta Platforms', price: 585.30, changePercent: 2.17 },
    ];
    return raw.map(stock => ({ ...stock, sparklineData: generateSparklineData(stock.changePercent) }));
  }, []);

  const cryptoMovers = useMemo(() => {
    const raw = [
      { symbol: 'BTC', name: 'Bitcoin', price: 101250.00, changePercent: 4.67 },
      { symbol: 'ETH', name: 'Ethereum', price: 3845.20, changePercent: 4.25 },
      { symbol: 'SOL', name: 'Solana', price: 224.50, changePercent: 8.95 },
      { symbol: 'XRP', name: 'Ripple', price: 2.34, changePercent: 13.59 },
      { symbol: 'DOGE', name: 'Dogecoin', price: 0.412, changePercent: 12.26 },
      { symbol: 'ADA', name: 'Cardano', price: 1.08, changePercent: 9.09 },
    ];
    return raw.map(crypto => ({ ...crypto, sparklineData: generateSparklineData(crypto.changePercent) }));
  }, []);

  const marketIndices = [
    { name: 'S&P 500', value: 6047.15, change: 1.24, positive: true },
    { name: 'NASDAQ', value: 19480.35, change: 1.67, positive: true },
    { name: 'DOW', value: 44285.72, change: -0.32, positive: false },
  ];

  const cryptoIndices = [
    { name: 'BTC', value: 101250, change: 4.52, positive: true },
    { name: 'ETH', value: 3845.20, change: 4.25, positive: true },
    { name: 'SOL', value: 224.50, change: 8.95, positive: true },
  ];

  const marketNews = [
    { id: 1, title: 'Fed signals potential rate cuts in 2025 as inflation cools', source: 'Reuters', time: '2h ago', category: 'macro', sentiment: 'bullish' },
    { id: 2, title: 'NVIDIA announces next-gen Blackwell Ultra chips', source: 'Bloomberg', time: '3h ago', category: 'tech', sentiment: 'bullish' },
    { id: 3, title: 'Tesla faces headwinds in China EV market', source: 'WSJ', time: '4h ago', category: 'auto', sentiment: 'bearish' },
  ];

  const cryptoNews = [
    { id: 1, title: 'Bitcoin surges past $100K as institutional adoption accelerates', source: 'CoinDesk', time: '1h ago', category: 'btc', sentiment: 'bullish' },
    { id: 2, title: 'Ethereum ETF sees record inflows amid rally', source: 'The Block', time: '2h ago', category: 'eth', sentiment: 'bullish' },
    { id: 3, title: 'Solana DeFi TVL hits new all-time high', source: 'DeFi Llama', time: '3h ago', category: 'sol', sentiment: 'bullish' },
  ];

  const cryptoIcons = { BTC: '₿', ETH: 'Ξ', SOL: '◎', XRP: '✕', DOGE: 'Ð', ADA: '◈' };

  // Fetch economic data
  useEffect(() => {
    fetchEconomicData();
  }, []);

  const fetchEconomicData = async () => {
    setEconomicLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/economic/summary`);
      if (response.ok) {
        const data = await response.json();
        setEconomicData({
          gdp: { value: data.gdp?.value || 2.8, trend: data.gdp?.trend || 'up' },
          inflation: { value: data.cpi?.value || 3.2, trend: data.cpi?.trend || 'down' },
          fedRate: { value: data.fedRate?.value || 5.25, trend: data.fedRate?.trend || 'stable' },
          treasury10y: { value: 4.42, trend: 'up' },
        });
      } else {
        throw new Error('API error');
      }
    } catch (error) {
      setEconomicData({
        gdp: { value: 2.8, trend: 'up' },
        inflation: { value: 3.2, trend: 'down' },
        fedRate: { value: 5.25, trend: 'stable' },
        treasury10y: { value: 4.42, trend: 'up' },
      });
    } finally {
      setEconomicLoading(false);
    }
  };

  const getEconTrendIcon = (trend) => {
    if (trend === 'up') return <ArrowUpRight size={14} className="trend-up" />;
    if (trend === 'down') return <ArrowDownRight size={14} className="trend-down" />;
    return <span className="trend-stable">—</span>;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getMarketStatus = () => {
    const now = new Date();
    const day = now.getDay();
    const time = now.getHours() * 60 + now.getMinutes();
    
    if (day === 0 || day === 6) return { isOpen: false, status: 'Closed', next: 'Opens Monday 9:30 AM' };
    if (time < 570) return { isOpen: false, status: 'Pre-Market', next: `Opens in ${Math.floor((570-time)/60)}h ${(570-time)%60}m` };
    if (time >= 960) return { isOpen: false, status: 'After Hours', next: 'Opens tomorrow 9:30 AM' };
    return { isOpen: true, status: 'Open', next: `Closes in ${Math.floor((960-time)/60)}h ${(960-time)%60}m` };
  };

  const marketStatus = getMarketStatus();
  
  // Navigate to stock research page
  const handleStockClick = (symbol) => {
    navigate(`/stock/${symbol}`);
  };
  
  // Navigate to fullscreen chart
  const handleChartClick = (symbol, e) => {
    e.stopPropagation();
    if (onOpenModal) onOpenModal(symbol);
  };

  const [tipIndex] = useState(() => Math.floor(Math.random() * 4));
  const tips = [
    { term: 'RSI (Relative Strength Index)', explanation: 'Momentum indicator. Above 70 = overbought, below 30 = oversold.' },
    { term: 'P/E Ratio', explanation: 'Price-to-Earnings. Lower may indicate undervaluation.' },
    { term: 'Market Cap', explanation: 'Large cap (>$10B), Mid cap ($2-10B), Small cap (<$2B).' },
    { term: 'Volume', explanation: 'High volume confirms moves; low volume = weak conviction.' },
  ];

  return (
    <div className="dashboard-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>{getGreeting()}</h1>
          <p className="header-date">{formatDate()}</p>
        </div>
        <div className="header-right">
          <div className={`market-status ${marketStatus.isOpen ? 'open' : 'closed'}`}>
            <span className="status-dot"></span>
            <span className="status-text">{marketStatus.status}</span>
            <span className="status-next">{marketStatus.next}</span>
          </div>
        </div>
      </header>

      {/* Political Intelligence Navigation */}
      <PoliticalIntelligenceNav navigate={navigate} />

      {/* Customizer */}
      <WidgetCustomizer
        widgets={widgets}
        onToggle={toggleWidget}
        isOpen={showCustomizer}
        onToggleOpen={() => setShowCustomizer(!showCustomizer)}
        onResetLayout={handleResetLayout}
      />

      {/* Row 1: Market Overview */}
      <section className="dashboard-row row-market-overview">
        {/* Indices Card - Always visible */}
        <div className="card card-indices">
          <div className="indices-grid">
            <div className="indices-section">
              <div className="indices-header">
                <h3>Markets</h3>
                <button className="link-btn" onClick={() => navigate('/markets')}>View All</button>
              </div>
              {marketIndices.map((idx) => (
                <div key={idx.name} className="index-row">
                  <span className="index-name">{idx.name}</span>
                  <span className="index-value">{formatPrice(idx.value, { decimals: 0 })}</span>
                  <span className={`index-change ${idx.positive ? 'positive' : 'negative'}`}>
                    {idx.positive ? '+' : ''}{idx.change.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
            <div className="indices-divider"></div>
            <div className="indices-section">
              <div className="indices-header">
                <h3>Crypto</h3>
                <button className="link-btn" onClick={() => navigate('/crypto')}>View All</button>
              </div>
              {cryptoIndices.map((idx) => (
                <div key={idx.name} className="index-row">
                  <span className="index-name">{idx.name}</span>
                  <span className="index-value">{formatPrice(idx.value, { decimals: idx.value < 100 ? 2 : 0 })}</span>
                  <span className={`index-change ${idx.positive ? 'positive' : 'negative'}`}>
                    {idx.positive ? '+' : ''}{idx.change.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fear & Greed */}
        {widgets.fearGreed.enabled && (
          <div className="card card-fear-greed">
            <div className="card-header">
              <h3>Fear & Greed</h3>
              <span className="badge badge-live">LIVE</span>
            </div>
            <FearGreedGauge value={72} />
          </div>
        )}

        {/* Economic Snapshot */}
        {widgets.economic.enabled && (
          <div className="card card-economic">
            <div className="card-header">
              <h3>Economic Snapshot</h3>
              <button className="icon-btn" onClick={fetchEconomicData} disabled={economicLoading}>
                <RefreshCw size={14} className={economicLoading ? 'spinning' : ''} />
              </button>
            </div>
            <div className="economic-grid">
              <div className="econ-item">
                <span className="econ-label">GDP</span>
                <div className="econ-value-row">
                  <span className="econ-value">{economicData?.gdp?.value || 2.8}%</span>
                  {getEconTrendIcon(economicData?.gdp?.trend)}
                </div>
              </div>
              <div className="econ-item">
                <span className="econ-label">Inflation</span>
                <div className="econ-value-row">
                  <span className="econ-value">{economicData?.inflation?.value || 3.2}%</span>
                  {getEconTrendIcon(economicData?.inflation?.trend)}
                </div>
              </div>
              <div className="econ-item">
                <span className="econ-label">Fed Rate</span>
                <div className="econ-value-row">
                  <span className="econ-value">{economicData?.fedRate?.value || 5.25}%</span>
                  {getEconTrendIcon(economicData?.fedRate?.trend)}
                </div>
              </div>
              <div className="econ-item">
                <span className="econ-label">10Y Yield</span>
                <div className="econ-value-row">
                  <span className="econ-value">{economicData?.treasury10y?.value || 4.42}%</span>
                  {getEconTrendIcon(economicData?.treasury10y?.trend)}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Row 2: Movers */}
      <section className="dashboard-row row-movers">
        {widgets.stockMovers.enabled && (
          <div className="card card-movers">
            <div className="card-header">
              <h3>Stock Movers</h3>
              <button className="link-btn" onClick={() => navigate('/markets')}>View All</button>
            </div>
            <div className="movers-list">
              {marketMovers.slice(0, 5).map((stock) => (
                <div key={stock.symbol} className="mover-row" onClick={() => handleStockClick(stock.symbol)}>
                  <div className="mover-info">
                    <span className="mover-symbol">{stock.symbol}</span>
                    <span className="mover-name">{stock.name}</span>
                  </div>
                  <div className="mover-chart">
                    <Sparkline data={stock.sparklineData} color="auto" width={60} height={24} />
                  </div>
                  <div className="mover-price">
                    <span className="price">{formatPrice(stock.price)}</span>
                    <span className={`change ${stock.changePercent >= 0 ? 'positive' : 'negative'}`}>
                      {stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%
                    </span>
                  </div>
                  <button className="mover-chart-btn" onClick={(e) => handleChartClick(stock.symbol, e)} title="Full Chart">
                    <ExternalLink size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {widgets.whyMoving.enabled && (
          <WhyMovingCard 
            trackedSymbols={whyMovingSymbols}
            onUpdateSymbols={setWhyMovingSymbols}
            allMovers={[...marketMovers, ...cryptoMovers]}
            onStockClick={handleStockClick}
          />
        )}

        {widgets.cryptoMovers.enabled && (
          <div className="card card-movers">
            <div className="card-header">
              <h3>Crypto Movers</h3>
              <button className="link-btn" onClick={() => navigate('/crypto')}>View All</button>
            </div>
            <div className="movers-list">
              {cryptoMovers.slice(0, 5).map((crypto) => (
                <div key={crypto.symbol} className="mover-row" onClick={() => handleStockClick(crypto.symbol)}>
                  <div className="mover-info">
                    <span className="mover-symbol">
                      <span className={`crypto-icon ${crypto.symbol.toLowerCase()}`}>
                        {cryptoIcons[crypto.symbol]}
                      </span>
                      {crypto.symbol}
                    </span>
                    <span className="mover-name">{crypto.name}</span>
                  </div>
                  <div className="mover-chart">
                    <Sparkline data={crypto.sparklineData} color="auto" width={60} height={24} />
                  </div>
                  <div className="mover-price">
                    <span className="price">{formatPrice(crypto.price)}</span>
                    <span className={`change ${crypto.changePercent >= 0 ? 'positive' : 'negative'}`}>
                      {crypto.changePercent >= 0 ? '+' : ''}{crypto.changePercent.toFixed(2)}%
                    </span>
                  </div>
                  <button className="mover-chart-btn" onClick={(e) => handleChartClick(crypto.symbol, e)} title="Full Chart">
                    <ExternalLink size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Row 3: News & Insights */}
      <section className="dashboard-row row-news">
        {widgets.marketNews.enabled && (
          <div className="card card-news">
            <div className="card-header">
              <h3>Market News</h3>
              <button className="link-btn" onClick={() => navigate('/news')}>View All <ExternalLink size={12} /></button>
            </div>
            <div className="news-list">
              {marketNews.map((news) => (
                <article key={news.id} className="news-item">
                  <div className="news-meta">
                    <span className={`news-tag ${news.category}`}>{news.category}</span>
                    <span className="news-source">{news.source}</span>
                    <span className="news-time"><Clock size={10} /> {news.time}</span>
                  </div>
                  <h4 className="news-title">{news.title}</h4>
                  <span className={`news-sentiment ${news.sentiment}`}>
                    {news.sentiment === 'bullish' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {news.sentiment}
                  </span>
                </article>
              ))}
            </div>
          </div>
        )}

        {widgets.aiInsights.enabled && (
          <div className="card card-insights">
            <div className="card-header">
              <h3><Zap size={16} /> AI Research Insights</h3>
            </div>
            <div className="insights-list">
              <div className="insight-item" onClick={() => handleStockClick('NVDA')}>
                <span className="insight-badge bullish">Bullish</span>
                <p><strong>NVDA</strong> showing strong momentum after earnings. RSI at 65, room to run.</p>
              </div>
              <div className="insight-item" onClick={() => handleStockClick('TSLA')}>
                <span className="insight-badge bearish">Bearish</span>
                <p><strong>TSLA</strong> facing resistance at {formatPrice(360)}. Volume declining.</p>
              </div>
              <div className="insight-item" onClick={() => handleStockClick('AAPL')}>
                <span className="insight-badge neutral">Neutral</span>
                <p><strong>AAPL</strong> consolidating. Support {formatPrice(185)}, resistance {formatPrice(195)}.</p>
              </div>
            </div>
            <button className="btn-gradient" onClick={() => navigate('/analysis')}>Get Full AI Analysis</button>
          </div>
        )}

        {widgets.cryptoNews.enabled && (
          <div className="card card-news">
            <div className="card-header">
              <h3>Crypto News</h3>
              <button className="link-btn" onClick={() => navigate('/crypto')}>View All <ExternalLink size={12} /></button>
            </div>
            <div className="news-list">
              {cryptoNews.map((news) => (
                <article key={news.id} className="news-item">
                  <div className="news-meta">
                    <span className={`news-tag ${news.category}`}>{news.category}</span>
                    <span className="news-source">{news.source}</span>
                    <span className="news-time"><Clock size={10} /> {news.time}</span>
                  </div>
                  <h4 className="news-title">{news.title}</h4>
                  <span className={`news-sentiment ${news.sentiment}`}>
                    {news.sentiment === 'bullish' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {news.sentiment}
                  </span>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Row 4: Additional Widgets */}
      {(widgets.researchTip.enabled || widgets.earningsCalendar.enabled || widgets.institutional.enabled || widgets.policyWatch.enabled || widgets.watchlist.enabled) && (
        <section className="dashboard-row row-extras">
          {widgets.researchTip.enabled && (
            <div className="card card-tip">
              <div className="card-header">
                <h3><Lightbulb size={16} /> Research Tip</h3>
                <span className="badge badge-green">Learn</span>
              </div>
              <h4 className="tip-term">{tips[tipIndex].term}</h4>
              <p className="tip-explanation">{tips[tipIndex].explanation}</p>
            </div>
          )}

          {widgets.earningsCalendar.enabled && (
            <EarningsCalendarCard
              trackedSymbols={earningsSymbols}
              onUpdateSymbols={setEarningsSymbols}
              onStockClick={handleStockClick}
            />
          )}

          {widgets.institutional.enabled && (
            <div className="card card-institutional clickable-card" onClick={() => navigate('/institutional')}>
              <div className="card-header">
                <h3><Building2 size={16} /> Institutional Moves</h3>
                <button className="link-btn" onClick={(e) => { e.stopPropagation(); navigate('/institutional'); }}>
                  View All <ChevronRight size={12} />
                </button>
              </div>
              <div className="inst-list">
                <div className="inst-row" onClick={(e) => { e.stopPropagation(); handleStockClick('AAPL'); }}>
                  <Building2 size={14} />
                  <span className="inst-name">Berkshire</span>
                  <span className="inst-action positive">+$2.1B AAPL</span>
                </div>
                <div className="inst-row" onClick={(e) => { e.stopPropagation(); handleStockClick('NVDA'); }}>
                  <Building2 size={14} />
                  <span className="inst-name">BlackRock</span>
                  <span className="inst-action positive">+$890M NVDA</span>
                </div>
                <div className="inst-row" onClick={(e) => { e.stopPropagation(); handleStockClick('TSLA'); }}>
                  <Building2 size={14} />
                  <span className="inst-name">Citadel</span>
                  <span className="inst-action negative">-$450M TSLA</span>
                </div>
              </div>
              <div className="card-footer-link">
                <span>Explore 13F filings & holdings</span>
                <ChevronRight size={14} />
              </div>
            </div>
          )}

          {widgets.policyWatch.enabled && (
            <div className="card card-policy clickable-card" onClick={() => navigate('/congress')}>
              <div className="card-header">
                <h3><Landmark size={16} /> Congress & Policy</h3>
                <button className="link-btn" onClick={(e) => { e.stopPropagation(); navigate('/congress'); }}>
                  View All <ChevronRight size={12} />
                </button>
              </div>
              <div className="policy-list">
                <div className="policy-row congress-highlight">
                  <Landmark size={14} />
                  <span className="policy-type">PELOSI</span>
                  <span className="policy-detail congress-trade">+$3M NVDA Calls • Jan 28</span>
                </div>
                <div className="policy-row congress-highlight">
                  <Landmark size={14} />
                  <span className="policy-type">TUBERVILLE</span>
                  <span className="policy-detail congress-trade">+$375K LMT • Jan 26</span>
                </div>
                <div className="policy-row">
                  <FileText size={14} />
                  <span className="policy-type">FED</span>
                  <span className="policy-detail">Rate Decision -25bps • Dec 18</span>
                </div>
              </div>
              <div className="card-footer-link">
                <span>Track Congress trades & disclosures</span>
                <ChevronRight size={14} />
              </div>
            </div>
          )}

          {widgets.watchlist.enabled && (
            <div className="card card-watchlist">
              <div className="card-header">
                <h3>Watchlist</h3>
                <button className="link-btn" onClick={() => navigate('/portfolio')}>View All</button>
              </div>
              <div className="watchlist-list">
                {[
                  { symbol: 'AAPL', price: 191.24, change: 1.15 },
                  { symbol: 'GOOGL', price: 176.82, change: -0.45 },
                  { symbol: 'NVDA', price: 142.50, change: 3.81 },
                ].map(s => (
                  <div key={s.symbol} className="watchlist-row" onClick={() => handleStockClick(s.symbol)}>
                    <span className="watchlist-symbol">{s.symbol}</span>
                    <span className="watchlist-price">{formatPrice(s.price)}</span>
                    <span className={`watchlist-change ${s.change >= 0 ? 'positive' : 'negative'}`}>
                      {s.change >= 0 ? '+' : ''}{s.change.toFixed(2)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default DashboardPage;